/**
 * A stable per-browser id, sent as `x-device-id` on every request.
 *
 * The API's per-device rules skip themselves when the header is absent, so
 * sending it is what switches them on. Constraints it enforces: non-empty and
 * at most 200 characters, or the value is discarded and the rule goes back to
 * being inert. A UUID is 36.
 *
 * Stored in `localStorage`, not `sessionStorage` where the token lives: the
 * token is deliberately scoped to one tab's session, whereas a device id that
 * changed on every sign-in would make the device rules useless.
 */

const DEVICE_ID_KEY = "ADMIN_APP_DEVICE_ID";

function randomId(): string {
  const uuid = globalThis.crypto?.randomUUID;

  // Absent on older browsers, and on any non-HTTPS origin other than
  // localhost, where `crypto` is not exposed as a secure context.
  if (typeof uuid === "function") return globalThis.crypto.randomUUID();

  const getRandomValues = globalThis.crypto?.getRandomValues;

  if (typeof getRandomValues === "function") {
    const bytes = new Uint8Array(16);

    globalThis.crypto.getRandomValues(bytes);

    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
      ""
    );
  }

  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

/** Cached so a blocked localStorage still yields one id per page load. */
let cached: string | null = null;

export function getDeviceId(): string {
  if (cached) return cached;

  try {
    const stored = localStorage.getItem(DEVICE_ID_KEY);

    if (stored) {
      cached = stored;
      return stored;
    }
  } catch {
    // Storage disabled (Safari private browsing throws on access). Fall
    // through and mint an id for this page load rather than failing the
    // request that wanted the header.
  }

  const created = randomId();

  cached = created;

  try {
    localStorage.setItem(DEVICE_ID_KEY, created);
  } catch {
    // As above — an id that lasts one page load is still better than none.
  }

  return created;
}
